import {
    buildStreetLabelMap,
    householdLabel,
    nameLabel,
} from '../adressHelpers/buildStreetLabels'
import { HOUSEHOLD_FIELDS, formatHouseholdField, isFixedFieldTitle } from '../adressHelpers/householdFields'
// Dezelfde labellijst als de tabel en de contactmomenten zelf; een tweede
// kopie zou stil uiteenlopen zodra er een uitkomst bij komt.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
import { OUTCOME_LABELS } from '../../components/adresses/contactMoments/contactMomentLabels'

/**
 * Zet het huishoudenoverzicht om naar exportrijen: één rij per huishouden, met
 * de kolommen van de tabel én elk eigen gegeven van het tabblad 'Gegevens'.
 *
 * Twee dingen die dit anders doet dan het scherm:
 *
 * 1. **Geen em-streepjes.** In de tabel is '—' de weergave van 'niet ingevuld',
 *    zodat de cel uitlijnt met zijn kop. In een CSV is het gewone tekst die
 *    iemand gaat filteren of optellen, dus leeg blijft hier leeg.
 *    AI-CONTEXT: See docs/pitfalls.md#ui-display-fallback-used-as-sort-key
 * 2. **Geen samengestelde cellen.** 'Laatste contact' staat in de tabel als
 *    datum + uitkomst + reeks in één cel; hier zijn dat drie kolommen, want in
 *    een spreadsheet is één waarde per cel het verschil tussen filteren en
 *    overtypen.
 *
 * De kolommen die de organisatie in het overzicht heeft uitgezet gaan hier
 * WÉL mee: dat is een weergavekeuze voor het scherm, en een export die er stil
 * gegevens uit laat is een export die niet klopt.
 *
 * AI-CONTEXT: See docs/patterns.md#ui-csv-export
 */

/**
 * De kolommen die er altijd zijn, in de volgorde van de tabel.
 *
 * `Straat` en `Naam` zijn samengesteld uit PersonaData (`Straatnaam` +
 * `Huisnummer`, en `Voornaam`/`Achternaam` of `Naam`). Die titels krijgen
 * verderop geen eigen kolom meer — zie `OWNED_DATA_TITLES`.
 *
 * @type {Array<string>}
 */
const HOUSEHOLD_FIXED_COLUMNS = [
    'Straat',
    'Naam',
    'Status',
    'Aanmelddatum',
    'Laatste contact',
    'Uitkomst laatste contact',
    'Mislukte pogingen',
    'Volgende afspraak',
    'Open taken',
    'Totaal taken',
    'Toegewezen aan',
]

/**
 * De vaste gegevens als eigen kolommen, met hun label van de kaart 'Vaste
 * gegevens'. Naam en straat staan al vooraan ('Naam', 'Straat' met nummer en
 * toevoeging); de rest komt hier. Een oude gegevensregel met de titel van een
 * vast gegeven wordt geen losse kolom meer — anders staat hetzelfde twee keer
 * in de rij, en na de migratie de oude kolom leeg.
 * AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T9)
 *
 * @type {Array<string>}
 */
const FIXED_FIELD_KEYS = Object.keys(HOUSEHOLD_FIELDS)
    .filter((field) => HOUSEHOLD_FIELDS[field] !== HOUSEHOLD_FIELDS.Naam && HOUSEHOLD_FIELDS[field] !== HOUSEHOLD_FIELDS.Straatnaam)

/** De koppen van die kolommen, in dezelfde volgorde. @type {Array<string>} */
const HOUSEHOLD_FIXED_FIELD_COLUMNS = FIXED_FIELD_KEYS.map((field) => HOUSEHOLD_FIELDS[field].label)

/**
 * Een datum als jjjj-mm-dd, of leeg.
 *
 * Bewust niet via `Intl`: een spreadsheet sorteert '2 maart 2026' als tekst, en
 * de npm-node in deze repo draait op small-icu waardoor `.format()` per
 * omgeving kan verschillen.
 * AI-CONTEXT: See docs/pitfalls.md#dev-measurement-script-turns-a-typo-into-a-finding
 *
 * @param {*} value - Firestore-tijdstempel, Date, of iets waar `new Date` mee kan
 * @returns {string}
 */
const isoDate = (value) => {
    if (!value) return ''
    const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value)
    if (Number.isNaN(date.getTime())) return ''

    // Op lokale datumdelen, niet via toISOString(): dat laatste is UTC, dus een
    // tijdstempel van half twee 's nachts in de zomertijd komt er een dag
    // eerder uit. De periodefilter van het exportvenster rekent wél in lokale
    // dagen, dus dan valt een huishouden binnen 'vanaf 2 maart' terwijl er
    // 1 maart in de cel staat. Zelfde reden als in ExportModal.jsx.
    const maand = String(date.getMonth() + 1).padStart(2, '0')
    const dag = String(date.getDate()).padStart(2, '0')
    return `${date.getFullYear()}-${maand}-${dag}`
}

/**
 * @typedef {object} HouseholdExportContext
 * @property {Array<object>} [personaData] - Alle `PersonaData`-regels van de organisatie
 * @property {Record<string, string>} [statusLabels] - Per statussleutel het label van deze organisatie
 * @property {Record<string, {timestamp: *, outcome: string, failedStreak: number}>} [latestContact] - Uitkomst van `latestContactPerAdress`
 * @property {Record<string, {Timestamp: *}>} [nextVisit] - Uitkomst van `nextVisitPerAdress`
 * @property {Record<string, {open: number, totaal: number}>} [openTasks] - Uitkomst van `openTasksPerAdress`
 * @property {Record<string, string>} [personaNameById] - Deelnemersnaam per ID, voor 'Toegewezen aan'
 */

/**
 * @typedef {object} HouseholdExportResult
 * @property {Array<Record<string, string|number>>} rows - Eén rij per huishouden
 * @property {Array<string>} header - De kolomvolgorde voor `downloadCSV`
 */

/**
 * Welke eigen gegevens een kolom krijgen, en in welke volgorde.
 *
 * Alleen titels waar bij minstens één van de meegegeven huishoudens ook echt
 * iets onder staat. Reden: bij een import belandt een WAARDE nog wel eens in
 * het titelveld — bij Duurzame Vecht 26 keer, met koppen als '0622592676' en
 * 'Kelly van Dam'. Die regels hebben zelf geen waarde, dus zonder deze grens
 * levert de export 26 lege kolommen op met een telefoonnummer als kop.
 *
 * Volgorde: de laagste `Position` waarop de titel voorkomt. Twee titels kunnen
 * dezelfde positie hebben (elk huishouden telt vanaf 1), dus het gelijkspel
 * wordt alfabetisch gebroken — anders hangt de kolomvolgorde af van de
 * toevallige volgorde van de snapshot.
 * AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily
 *
 * @param {Array<object>} personaData - Regels van de meegegeven huishoudens
 * @returns {Array<string>}
 */
const dataColumnsFrom = (personaData) => {

    /** @type {Map<string, number>} */
    const positions = new Map()

    personaData.forEach((row) => {
        const title = row?.Title
        if (typeof title !== 'string' || title.trim() === '') return
        if (isFixedFieldTitle(title)) return
        if (String(row?.Value ?? '').trim() === '') return

        // Number() en niet Number.isFinite(row.Position): Position staat er niet
        // overal als getal in, en een tekst-'2' zou dan achteraan belanden.
        const ruwePositie = Number(row?.Position)
        const position = Number.isFinite(ruwePositie) ? ruwePositie : Number.MAX_SAFE_INTEGER
        if (!positions.has(title) || position < positions.get(title)) {
            positions.set(title, position)
        }
    })

    return [...positions.entries()]
        .sort((a, b) => (a[1] - b[1]) || a[0].localeCompare(b[0], 'nl'))
        .map(([title]) => title)
}

/**
 * @param {Array<object>} [adresses] - De huishoudens die mee moeten, al gefilterd
 * @param {HouseholdExportContext} [context]
 * @returns {HouseholdExportResult}
 */
const buildHouseholdExportRows = (adresses, context = {}) => {
    const households = Array.isArray(adresses) ? adresses : []
    if (households.length === 0) return { rows: [], header: [] }

    const {
        personaData = [],
        statusLabels = {},
        latestContact = {},
        nextVisit = {},
        openTasks = {},
        personaNameById = {},
    } = context

    // Alleen de regels van de huishoudens die meegaan: exporteert iemand één
    // rij, dan hoort daar niet de hele veldenlijst van de organisatie omheen.
    const ids = new Set(households.map((item) => item?.ID))
    const relevantData = (Array.isArray(personaData) ? personaData : [])
        .filter((row) => ids.has(row?.PersonaID))

    const fieldsByAddressId = buildStreetLabelMap(relevantData, households)
    const dataColumns = dataColumnsFrom(relevantData)

    const rows = households.map((item) => {
        const contact = latestContact[item?.ID]
        const tasks = openTasks[item?.ID]
        const fields = fieldsByAddressId[item?.ID] || {}

        /** @type {Record<string, string|number>} */
        const row = {
            Straat: householdLabel(fieldsByAddressId, item, ''),
            Naam: nameLabel(fieldsByAddressId, item?.ID, ''),
            Status: statusLabels[item?.Status] || item?.Status || '',
            Aanmelddatum: isoDate(item?.Timestamp),
            // Geen contactmoment is iets anders dan een mislukte poging: daar
            // hoort leeg te staan en geen 0.
            // AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
            'Laatste contact': contact ? isoDate(contact.timestamp) : '',
            'Uitkomst laatste contact': contact
                ? (OUTCOME_LABELS[contact.outcome] || contact.outcome || '')
                : '',
            'Mislukte pogingen': contact ? (contact.failedStreak || 0) : '',
            'Volgende afspraak': isoDate(nextVisit[item?.ID]?.Timestamp),
            // Taken zijn wél te tellen zonder dat er iets ontbreekt: een
            // huishouden zonder taken heeft er nul.
            'Open taken': tasks?.open ?? 0,
            'Totaal taken': tasks?.totaal ?? 0,
            'Toegewezen aan': (Array.isArray(item?.AssignedTo) ? item.AssignedTo : [])
                .map((personaId) => personaNameById[personaId] || 'Onbekende deelnemer')
                .join(', '),
        }

        // Elke kolom op elke rij, ook leeg: een ontbrekende sleutel zou de
        // cellen van die ene rij een plek laten opschuiven.
        // `?? ''` en niet `|| ''`: een 0 of een false is een antwoord van de
        // invuller, geen leeg veld.
        FIXED_FIELD_KEYS.forEach((field) => {
            row[HOUSEHOLD_FIELDS[field].label] = formatHouseholdField(field, fields[field])
        })
        dataColumns.forEach((title) => { row[title] = fields[title] ?? '' })

        return row
    })

    return { rows, header: [...HOUSEHOLD_FIXED_COLUMNS, ...HOUSEHOLD_FIXED_FIELD_COLUMNS, ...dataColumns] }
}

export { HOUSEHOLD_FIXED_COLUMNS, HOUSEHOLD_FIXED_FIELD_COLUMNS }
export default buildHouseholdExportRows
